import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Shield,
  Plus,
  Search,
  Edit,
  Trash2,
  Clock,
  AlertTriangle,
  RefreshCw,
  Lock,
  Calendar,
  User,
  Building,
  CheckCircle,
  Filter
} from 'lucide-react';
import { accessControlApi } from '@domains/admin/api/accessControl.api';
import { notify } from '@core/notifications/notify';
import { useModal, ConfirmModal, AccessRuleModal, ExpiryConfigModal } from '@components/modal';
import type {
  AccessControlRule,
  ExpiryConfig,
  ExpiringItem,
} from '@domains/admin/api/accessControl.adapter';
import PageInfoButton from '@/components/PageInfoButton';

const AccessControl: React.FC = () => {
  const { hasPermission, isLoading: authLoading } = useAuth();
  const { openModal } = useModal();
  const [activeTab, setActiveTab] = useState<'rules' | 'expiry' | 'expiring'>('rules');
  const [accessRules, setAccessRules] = useState<AccessControlRule[]>([]);
  const [expiryConfigs, setExpiryConfigs] = useState<ExpiryConfig[]>([]);
  const [expiringItems, setExpiringItems] = useState<ExpiringItem[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');


  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'rules') {
        await loadAccessRules();
      } else if (activeTab === 'expiry') {
        await loadExpiryConfigs();
      } else if (activeTab === 'expiring') {
        await loadExpiringItems();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadAccessRules = async () => {
    try {
      const result = await accessControlApi.getRules();
      setAccessRules(result.rules);
    } catch (error) {
      console.error('Error loading access rules:', error);
      notify.error('Failed to load access rules');
    }
  };

  const loadExpiryConfigs = async () => {
    try {
      const result = await accessControlApi.getExpiryConfigs();
      setExpiryConfigs(result.configs);
    } catch (error) {
      console.error('Error loading expiry configs:', error);
      notify.error('Failed to load expiry configs');
    }
  };

  const loadExpiringItems = async () => {
    try {
      const result = await accessControlApi.getExpiringItems();
      setExpiringItems([...result.users, ...result.companies]);
    } catch (error) {
      console.error('Error loading expiring items:', error);
      notify.error('Failed to load expiring items');
    }
  };

  const filteredRules = accessRules.filter(rule => {
    const matchesSearch = !searchTerm ||
      rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || rule.ruleType === filterType;
    return matchesSearch && matchesType;
  });

  const handleSaveRule = async (ruleData: any) => {
    try {
      if (ruleData.id) {
        await accessControlApi.updateRule(ruleData.id, ruleData);
      } else {
        await accessControlApi.createRule(ruleData);
      }
      notify.success('Access rule saved successfully!');
      loadAccessRules();
    } catch (error) {
      console.error('Error saving access rule:', error);
      notify.error('Failed to save access rule');
    }
  };

  const handleDeleteRule = (rule: AccessControlRule) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete Access Rule',
        message: `Are you sure you want to delete the access rule "${rule.name}"? This action cannot be undone.`,
        confirmText: 'Delete Rule',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: async () => {
          try {
            await accessControlApi.deleteRule(rule.id);
            notify.success('Access rule deleted successfully!');
            loadAccessRules();
          } catch (error) {
            console.error('Error deleting access rule:', error);
            notify.error('Failed to delete access rule');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleSaveExpiry = async (expiryData: any) => {
    try {
      if (expiryData.id) {
        await accessControlApi.updateExpiryConfig(expiryData.id, expiryData);
      } else {
        await accessControlApi.createExpiryConfig(expiryData);
      }
      notify.success('Expiry config saved successfully!');
      loadExpiryConfigs();
    } catch (error) {
      console.error('Error saving expiry config:', error);
      notify.error('Failed to save expiry config');
    }
  };

  const handleDeleteExpiry = (expiry: ExpiryConfig) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete Expiry Config',
        message: `Are you sure you want to delete the expiry configuration for ${expiry.expiryType}? This action cannot be undone.`,
        confirmText: 'Delete Config',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: async () => {
          try {
            await accessControlApi.deleteExpiryConfig(expiry.id);
            notify.success('Expiry config deleted successfully!');
            loadExpiryConfigs();
          } catch (error) {
            console.error('Error deleting expiry config:', error);
            notify.error('Failed to delete expiry config');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const getRuleTypeColor = (type: string) => {
    switch (type) {
      case 'ip_whitelist': return 'bg-emerald-100 text-emerald-700';
      case 'ip_blacklist': return 'bg-red-100 text-red-700';
      case 'time_restriction': return 'bg-amber-100 text-amber-700';
      case 'geo_restriction': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getExpiryTypeColor = (type: string) => {
    switch (type) {
      case 'user_access': return 'bg-blue-100 text-blue-700';
      case 'company_license': return 'bg-purple-100 text-purple-700';
      case 'document_retention': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getExpiringStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-amber-50 border-amber-200';
      default: return 'bg-emerald-50 border-emerald-200';
    }
  };

  const getExpiringStatusBadge = (status: string) => {
    switch (status) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'warning': return 'bg-amber-100 text-amber-700';
      default: return 'bg-emerald-100 text-emerald-700';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasPermission('system.config')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="bg-red-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-white" size={24} />
              <div>
                <h1 className="text-lg font-bold text-white">Access Denied</h1>
                <p className="text-red-100 text-sm mt-0.5">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-6 text-center">
            <p className="text-slate-600 text-sm">Only Super Admin can access Access Control.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Access Control</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm mt-1">Manage access rules and expiry configurations</p>
          </div>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-3xl border border-slate-100 p-1.5 mb-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('rules')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'rules'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Lock size={16} />
              Access Rules
            </button>
            <button
              onClick={() => setActiveTab('expiry')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'expiry'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Clock size={16} />
              Expiry Configs
            </button>
            <button
              onClick={() => setActiveTab('expiring')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'expiring'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <AlertTriangle size={16} />
              Expiring Items
            </button>
          </div>
        </div>

        {/* Access Rules Tab */}
        {activeTab === 'rules' && (
          <>
            {/* Search and Filter */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Search Rules</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="text"
                      placeholder="Search by name or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Rule Type</label>
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="">All Types</option>
                      <option value="ip_whitelist">IP Whitelist</option>
                      <option value="ip_blacklist">IP Blacklist</option>
                      <option value="time_restriction">Time Restriction</option>
                      <option value="geo_restriction">Geo Restriction</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      openModal({
                        component: AccessRuleModal,
                        props: {
                          onSubmit: handleSaveRule,
                          selectedRule: null,
                        },
                        options: { size: 'large' }
                      });
                    }}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={16} />
                    Add Rule
                  </button>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterType('');
                    }}
                    className="w-full px-4 py-2.5 bg-slate-100 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Rules List */}
            <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lock className="text-slate-400" size={18} />
                    <h3 className="text-sm font-semibold text-slate-900">Access Rules</h3>
                  </div>
                  <span className="px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-700">
                    {filteredRules.length}
                  </span>
                </div>
              </div>
              <div className="p-4">
                {filteredRules.length === 0 ? (
                  <div className="py-12">
                    <Lock className="text-slate-300 mx-auto mb-3" size={36} />
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">No access rules found</h3>
                    <p className="text-sm text-slate-500">
                      {searchTerm || filterType
                        ? 'Try adjusting your search criteria'
                        : 'Get started by creating your first access rule'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredRules.map(rule => (
                      <div
                        key={rule.id}
                        className="bg-slate-50 rounded-lg border border-slate-200 p-4 hover:border-blue-200 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                              <h3 className="font-medium text-slate-900">{rule.name}</h3>
                              <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getRuleTypeColor(rule.ruleType)}`}>
                                {rule.ruleType.replace('_', ' ').toUpperCase()}
                              </span>
                              <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${rule.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                {rule.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 mb-2">{rule.description}</p>
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Filter size={12} />
                                Priority: {rule.priority}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                Updated: {new Date(rule.updatedAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => {
                                openModal({
                                  component: AccessRuleModal,
                                  props: {
                                    onSubmit: handleSaveRule,
                                    selectedRule: rule,
                                  },
                                  options: { size: 'large' }
                                });
                              }}
                              className="p-2 bg-white border border-slate-200 text-slate-400 rounded-lg hover:border-blue-300 hover:text-blue-600 transition-colors"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteRule(rule)}
                              className="p-2 bg-white border border-slate-200 text-slate-400 rounded-lg hover:border-red-300 hover:text-red-600 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Expiry Configs Tab */}
        {activeTab === 'expiry' && (
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="text-slate-400" size={18} />
                  <h3 className="text-sm font-semibold text-slate-900">Expiry Configurations</h3>
                </div>
                <span className="px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-700">
                  {expiryConfigs.length}
                </span>
              </div>
            </div>
            <div className="p-4">
              {expiryConfigs.length === 0 ? (
                <div className="py-12">
                  <Clock className="text-slate-300 mx-auto mb-3" size={36} />
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">No expiry configs found</h3>
                  <p className="text-sm text-slate-500">
                    Get started by creating your first expiry configuration
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {expiryConfigs.map(config => (
                    <div
                      key={config.id}
                      className="bg-slate-50 rounded-lg border border-slate-200 p-4 hover:border-blue-200 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Clock className="text-blue-600" size={18} />
                        </div>
                        <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${config.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {config.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <h3 className="font-medium text-slate-900 mb-1">{config.name}</h3>
                      <p className="text-sm text-slate-500 mb-3 line-clamp-2">{config.description}</p>
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Type:</span>
                          <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getExpiryTypeColor(config.expiryType)}`}>
                            {config.expiryType.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Expiry:</span>
                          <span className="font-medium text-slate-900">{config.expiryDays} days</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Warning:</span>
                          <span className="font-medium text-amber-600">{config.warningDays} days</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
                        <button
                          onClick={() => {
                            openModal({
                              component: ExpiryConfigModal,
                              props: {
                                onSubmit: handleSaveExpiry,
                                selectedExpiry: config,
                              },
                              options: { size: 'large' }
                            });
                          }}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteExpiry(config)}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Expiring Items Tab */}
        {activeTab === 'expiring' && (
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="text-slate-400" size={18} />
                  <h3 className="text-sm font-semibold text-slate-900">Expiring Items</h3>
                </div>
                <span className="px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-700">
                  {expiringItems.length}
                </span>
              </div>
            </div>
            <div className="p-4">
              {expiringItems.length === 0 ? (
                <div className="py-12">
                  <CheckCircle className="text-emerald-400 mx-auto mb-3" size={36} />
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">No expiring items</h3>
                  <p className="text-sm text-slate-500">
                    All items are within their valid period
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {expiringItems.map(item => (
                    <div
                      key={item.id}
                      className={`rounded-lg border p-4 transition-colors ${getExpiringStatusColor(item.status)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${item.type === 'user' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                            {item.type === 'user' ? <User className="text-blue-600" size={18} /> : <Building className="text-purple-600" size={18} />}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-slate-900 mb-1">{item.name}</h3>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar size={13} />
                                Expires: {new Date(item.expiryDate).toLocaleDateString()}
                              </span>
                              <span className={`font-medium ${item.daysRemaining <= 7 ? 'text-red-600' : item.daysRemaining <= 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {item.daysRemaining} days remaining
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getExpiringStatusBadge(item.status)}`}>
                          {item.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessControl;
